/**
 * Helper functions for converting and formatting units of measurement
 * Converts imperial units (inches, feet) to metric (cm, meters) based on locale
 */

export type Locale = 'en' | 'pt' | 'es';

/**
 * Determines if the locale uses metric system
 */
export function usesMetricSystem(locale: Locale): boolean {
  return locale === 'pt' || locale === 'es';
}

/**
 * Converts inches to centimeters
 */
export function inchesToCm(inches: number): number {
  return inches * 2.54;
}

/**
 * Converts square feet to square meters
 */
export function sqFeetToSqMeters(sqFeet: number): number {
  return sqFeet * 0.092903;
}

/**
 * Formats a number with appropriate decimal places
 */
export function formatNumber(value: number, decimals: number = 2): string {
  const nearestInteger = Math.round(value);
  const integerRoundingThreshold = 0.02;

  if (
    !Number.isInteger(value) &&
    Math.abs(value - nearestInteger) <= integerRoundingThreshold
  ) {
    return nearestInteger.toString();
  }

  return value.toFixed(decimals).replace(/\.?0+$/, '');
}

/**
 * Converts and formats a spec value with units based on locale
 * Examples:
 * - "0.13 Inches" (en) -> "0.33 Centímetros" (pt)
 * - "12 Inches" (en) -> "30.48 Centímetros" (pt)
 * - "Square Feet Per Box" value conversion
 */
export function convertSpecValue(
  value: string,
  specKey: string,
  locale: Locale
): string {
  // Don't convert for English
  if (!usesMetricSystem(locale)) {
    return value;
  }

  // Pattern to match number followed by unit
  const inchesMatch = value.match(/^([\d.]+)\s*Inches?$/i);
  if (inchesMatch) {
    const inches = Number.parseFloat(inchesMatch[1]);
    const cm = inchesToCm(inches);
    const unit = locale === 'pt' ? 'Centímetros' : 'Centímetros';
    return `${formatNumber(cm)} ${unit}`;
  }

  // Match patterns like "12 x 24 Inches"
  const dimensionsMatch = value.match(/^([\d.]+)\s*x\s*([\d.]+)\s*Inches?$/i);
  if (dimensionsMatch) {
    const width = Number.parseFloat(dimensionsMatch[1]);
    const length = Number.parseFloat(dimensionsMatch[2]);
    const widthCm = inchesToCm(width);
    const lengthCm = inchesToCm(length);
    const unit = locale === 'pt' ? 'Centímetros' : 'Centímetros';
    return `${formatNumber(widthCm)} x ${formatNumber(lengthCm)} ${unit}`;
  }

  // Match "Square Feet"
  const sqFeetMatch = value.match(/^([\d.]+)\s*Square\s*Feet?$/i);
  if (sqFeetMatch) {
    const sqFeet = Number.parseFloat(sqFeetMatch[1]);
    const sqMeters = sqFeetToSqMeters(sqFeet);
    const unit = locale === 'pt' ? 'Metros Quadrados' : 'Metros Cuadrados';
    return `${formatNumber(sqMeters)} ${unit}`;
  }

  // If spec key contains "Width", "Length", "Thickness" and has just a number
  const dimensionKeys = [
    'Width',
    'Length',
    'Thickness',
    'Pattern Repeat Width',
    'Pattern Repeat Length',
  ];
  if (dimensionKeys.some((key) => specKey.includes(key))) {
    const numberMatch = value.match(/^([\d.]+)$/);
    if (numberMatch) {
      // Assume inches if it's a dimension key with just a number
      const inches = Number.parseFloat(numberMatch[1]);
      const cm = inchesToCm(inches);
      const unit = locale === 'pt' ? 'Centímetros' : 'Centímetros';
      return `${formatNumber(cm)} ${unit}`;
    }
  }

  // Return original value if no conversion needed
  return value;
}

/**
 * Gets the appropriate unit name for a spec key based on locale
 */
export function getUnitForSpec(specKey: string, locale: Locale): string {
  if (!usesMetricSystem(locale)) {
    return 'Inches';
  }

  if (specKey.includes('Square Feet')) {
    return locale === 'pt' ? 'Metros Quadrados' : 'Metros Cuadrados';
  }

  return locale === 'pt' ? 'Centímetros' : 'Centímetros';
}
